'use client';

import { handleAgentAction } from '@/_utils/ai';
import type { AssistantMessageProps } from './AssistantMessage.types';
import { type FC, useCallback, useEffect } from 'react';
import { Flex, Text, Icon } from '@/_components';
import { FormattedMessage } from './_components/FormattedMessage';
import styles from './AssistantMessage.module.css';
import { useCopy } from '@/_hooks/useCopy';
import { getClasses } from '@/_utils/classnames';

// Helper component for rendering assistant messages
export const AssistantMessage: FC<AssistantMessageProps> = ({
  message,
  onExampleClick,
  onFeedback,
  messageFeedback,
  isStreaming,
  onRegenerate,
  messages,
}) => {
  const { copyTextToClipboard } = useCopy();

  // Detect if the message is currently streaming by prop
  const isStreamingState = Boolean(isStreaming);

  // If message has an action that needs handling
  const handleAction = useCallback(async () => {
    if (!message.action) return;

    try {
      // Fix: pass only the action string
      const actionResult = handleAgentAction(message.action);
      console.log('Action result:', actionResult);
      // You can add UI feedback here if needed
    } catch (error) {
      console.error('Error executing action:', error);
    }
  }, [message.action]);

  const handleFeedbackClick = (feedback: 'up' | 'down') => {
    if (onFeedback && message.id) {
      onFeedback(message.id, feedback);
    }
  };

  const handleCopyClick = async () => {
    try {
      // Clean the content by removing streaming cursor if present
      const cleanContent =
        message.content?.replace(
          /<span class="[^"]*streamingCursor[^"]*">_<\/span>/g,
          '',
        ) || '';

      await copyTextToClipboard(cleanContent);
      // You could add a toast notification here if you have access to the toast context
      console.log('Message copied to clipboard');
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const handleRegenerateClick = async () => {
    if (!onRegenerate) return;

    // Find the last user message before this assistant message
    const messageIndex = messages.findIndex((msg) => msg.id === message.id);
    if (messageIndex > 0) {
      // Look backwards to find the last user message
      for (let i = messageIndex - 1; i >= 0; i--) {
        const prevMessage = messages[i];
        if (prevMessage.isUser || prevMessage.role === 'user') {
          await onRegenerate(prevMessage.content || '');
          break;
        }
      }
    }
  };

  const currentFeedback = message.id
    ? messageFeedback?.[message.id]
    : undefined;

  // Don't show feedback buttons for welcome messages
  const isWelcomeMessage = message.id?.startsWith('welcome-');

  useEffect(() => {
    if (message.action) {
      handleAction();
    }
  }, [message.action, handleAction]);

  // Check if content is effectively empty (only streaming cursor or truly empty)
  // The streaming cursor is a span with just an underscore: <span ...>_</span>
  // Strip all HTML tags and check if content is empty or just an underscore
  const contentWithoutTags = (message.content || '')
    .replace(/<[^>]*>/g, '')
    .trim();
  const hasContent =
    contentWithoutTags.length > 0 && contentWithoutTags !== '_';

  return (
    <>
      {!hasContent && isStreamingState ? (
        <Flex
          gap={8}
          isYCentered={true}
          className={styles.loadingMessageContainer}
        >
          <div className={styles.loadingIcon}>
            <div className={styles.spinner}></div>
          </div>
          <Flex isColumn={true} gap={2}>
            <Text color="secondary" fontWeight="medium">
              Getting your answer ready...
            </Text>
            <Text color="disabled" type="tiny">
              This may take a moment while we search through your data
            </Text>
          </Flex>
        </Flex>
      ) : (
        <FormattedMessage
          content={message.content || ''}
          questionnaireIds={
            !message.isLoading ? message.questionnaireIds : undefined
          }
          questionIds={!message.isLoading ? message.questionIds : undefined}
          questionnairePairs={
            !message.isLoading ? message.questionnairePairs : undefined
          }
          questionnaireMetadata={
            !message.isLoading ? message.questionnaireMetadata : undefined
          }
          graphql_data={!message.isLoading ? message.graphql_data : undefined}
          filters={!message.isLoading ? message.filters : undefined}
          onExampleClick={onExampleClick}
        />
      )}

      {/* Example suggestions */}
      {message.examples &&
        Array.isArray(message.examples) &&
        message.examples.length > 0 && (
          <div className={styles.examplesContainer}>
            <p className={styles.examplesHeader}>
              Try asking me questions like:
            </p>
            <ul className={styles.examplesList}>
              {message.examples.map((example, i) => (
                <li
                  key={i}
                  className={styles.exampleItem}
                  role="button"
                  tabIndex={0}
                  onClick={() => onExampleClick(example || '')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onExampleClick(example || '');
                    }
                  }}
                  aria-label={`Use example: ${example || ''}`}
                >
                  {example || ''}
                </li>
              ))}
            </ul>
          </div>
        )}

      {/* Suggestions - different styling from examples */}
      {message.suggestions &&
        Array.isArray(message.suggestions) &&
        message.suggestions.length > 0 && (
          <div className={styles.suggestionsContainer}>
            <p className={styles.suggestionsHeader}>
              Try asking me questions like:
            </p>
            <div className={styles.suggestionsList}>
              {message.suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => onExampleClick(suggestion || '')}
                  className={styles.suggestionButton}
                >
                  {suggestion || ''}
                </button>
              ))}
            </div>
          </div>
        )}

      {/* Feedback buttons - only show for non-welcome messages and when there are no suggestions */}
      {!isWelcomeMessage && !message.suggestions && !message.examples && (
        <div className={styles.feedbackContainer}>
          <button
            className={styles.feedbackButton}
            onClick={handleCopyClick}
            title="Copy"
          >
            <Icon name="copy" size={14} />
          </button>
          <button
            className={getClasses(styles.feedbackButton, {
              [styles.active]: currentFeedback === 'up',
            })}
            onClick={() => handleFeedbackClick('up')}
            title="Good response"
          >
            <Icon
              name="thumbs-up"
              size={14}
              color={currentFeedback === 'up' ? 'primary' : undefined}
            />
          </button>
          <button
            className={getClasses(styles.feedbackButton, {
              [styles.active]: currentFeedback === 'down',
            })}
            onClick={() => handleFeedbackClick('down')}
            title="Bad response"
          >
            <Icon
              name="thumbs-down"
              size={14}
              color={currentFeedback === 'down' ? 'primary' : undefined}
            />
          </button>
          {onRegenerate && (
            <button
              className={styles.feedbackButton}
              onClick={handleRegenerateClick}
              title="Regenerate"
            >
              <Icon name="refresh" size={14} />
            </button>
          )}

          {typeof message.responseDurationMs === 'number' &&
            !isStreamingState && (
              <span className={styles.responseDuration}>
                Responded in {(message.responseDurationMs / 1000).toFixed(1)}s
              </span>
            )}
        </div>
      )}
    </>
  );
};
